/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { authClientsSetHeaders } from "../funcs/authClientsSetHeaders.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { AuthClient } from "../models/components/authclient.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetAuthClientHeadersRequest,
  SetAuthClientHeadersSecurity,
} from "../models/operations/setauthclientheaders.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetAuthClientHeadersMutationVariables = {
  request: SetAuthClientHeadersRequest;
  security?: SetAuthClientHeadersSecurity | undefined;
  options?: RequestOptions;
};

export type SetAuthClientHeadersMutationData = AuthClient;

export type SetAuthClientHeadersMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setAuthClientHeaders authClients
 *
 * @remarks
 * Replace the headers an auth client sends upstream. Rotating a shared client's credential changes upstream behavior on every server that offers it. Token-generating clients produce their own Authorization header, which cannot be configured. Clients created from a server's existing settings follow those settings and are edited there. Takes effect only where typed auth client resolution is enforced. Requires org:admin.
 */
export function useSetAuthClientHeadersMutation(
  options?: MutationHookOptions<
    SetAuthClientHeadersMutationData,
    SetAuthClientHeadersMutationError,
    SetAuthClientHeadersMutationVariables
  >,
): UseMutationResult<
  SetAuthClientHeadersMutationData,
  SetAuthClientHeadersMutationError,
  SetAuthClientHeadersMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetAuthClientHeadersMutation(client, options),
    ...options,
  });
}

export function mutationKeySetAuthClientHeaders(): MutationKey {
  return ["@gram/client", "authClients", "setHeaders"];
}

export function buildSetAuthClientHeadersMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetAuthClientHeadersMutationVariables,
  ) => Promise<SetAuthClientHeadersMutationData>;
} {
  return {
    mutationKey: mutationKeySetAuthClientHeaders(),
    mutationFn: function setAuthClientHeadersMutationFn({
      request,
      security,
      options,
    }): Promise<SetAuthClientHeadersMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(authClientsSetHeaders(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
